import { multicallRetryUniversal } from '@1delta/providers'
import { termMaxConfigByChain } from '@1delta/data-sdk'
import type { TermMaxMarketConfig } from './types'

/**
 * Matured TermMax markets that can still hold a position someone forgot to exit.
 *
 * WHY THIS EXISTS. `fetchTermMaxMarkets` drops everything past maturity, which is
 * right for the PUBLIC surface — nobody can lend into a matured market — and wrong
 * for the USER surface: FT still redeems for the underlying after maturity and a GT
 * still carries an obligation, so a holder who never came back is simply invisible.
 *
 * WHY IT IS NOT JUST "every matured market". Ethereum carries 683 of them going
 * back to 2025-04, and reading all 706 markets through `getPositionDetails` reverts
 * on gas. But most are FULLY SETTLED: once the FT and GT supplies reach zero, no
 * position can exist in that market and none can ever appear again. Sweeping
 * `totalSupply` once per chain removes them for every user at once — measured
 * 2026-08-21: Ethereum 683 → 306, BNB 664 → 383, Arbitrum 203 → 123.
 *
 * The sweep is SHARED and the result is MONOTONIC — a market that reaches zero
 * outstanding cannot come back — so it is safe to cache for a long window and, on
 * failure, to keep serving the last answer rather than falling back to nothing.
 * Falling back to nothing is the one behaviour that would silently reintroduce the
 * bug this module exists to fix.
 */
const RESIDUE_TTL_MS = 60 * 60_000
/**
 * How long a FAILED refresh is remembered.
 *
 * Without this the sweep re-runs on every request whenever upstream is unwell,
 * which is exactly what happened: the history endpoint answered in 13 s, the
 * empty result was not cached, and every read paid it again. A failure is an
 * answer too — just a shorter-lived one.
 */
const RESIDUE_FAILURE_TTL_MS = 5 * 60_000
/**
 * The history endpoint is SLOW and variable — measured 1.0 s, 1.4 s, 3.2 s and
 * 13.3 s on consecutive calls. Nothing on a read path may wait that long, and
 * since the refresh is off the hot path a timeout only delays the next attempt.
 */
const API_TIMEOUT_MS = 4_000

interface ResidueEntry {
  markets: TermMaxMarketConfig[]
  at: number
  /** A refresh that failed — retried sooner, and never overwrites a good answer. */
  failed?: boolean
}

const residueCache = new Map<string, ResidueEntry>()
/** In-flight refreshes, so concurrent reads do not stampede the same sweep. */
const inFlight = new Map<string, Promise<void>>()

/** Test seam. */
export const __resetTermMaxResidue = () => {
  residueCache.clear()
  inFlight.clear()
}

const isStale = (e: ResidueEntry | undefined): boolean =>
  !e || Date.now() - e.at > (e.failed ? RESIDUE_FAILURE_TTL_MS : RESIDUE_TTL_MS)

/**
 * The residue for `chainId`, WITHOUT waiting for it.
 *
 * This is what read paths must call. The set is chain-scoped, shared by every
 * user and monotonic — it changes only when a market matures — so it has no
 * business being fetched inline, and fetching it inline is what made an Ethereum
 * read take ~15 s: the underlying history endpoint took 13 s of that, and both
 * the public fetch and the per-user build awaited it.
 *
 * A cold cache therefore returns EMPTY and refreshes in the background. That
 * degrades to live-only — today's behaviour, and the behaviour before matured
 * markets were covered at all — for the first read after a cold start, rather
 * than making every reader wait on a third party.
 */
export function getTermMaxMaturedResidue(chainId: string): TermMaxMarketConfig[] {
  const hit = residueCache.get(chainId)
  if (isStale(hit)) void refreshTermMaxMaturedResidue(chainId)
  return hit?.markets ?? []
}

/**
 * Refresh the residue, at most one sweep per chain at a time.
 *
 * Deliberately returns a promise a JOB can await while read paths do not — the
 * point is that nobody on a request path has to.
 */
export function refreshTermMaxMaturedResidue(chainId: string): Promise<void> {
  const running = inFlight.get(chainId)
  if (running) return running
  const p = sweep(chainId).finally(() => inFlight.delete(chainId))
  inFlight.set(chainId, p)
  return p
}

export const getCachedTermMaxResidue = (
  chainId: string,
): TermMaxMarketConfig[] | undefined => residueCache.get(chainId)?.markets

const API = 'https://api.termmax.ts.finance'

/**
 * The full market history for a chain, matured included.
 *
 * `/market/data` — what the public fetcher uses — is live-only and has no flag to
 * widen it (`includeMatured`, `matured`, `all`, `status` were all probed and are
 * ignored). `/market/config/market/list?includeInactive=true` is the route that
 * returns history: 706 rows on Ethereum against 19 from the default.
 */
async function fetchFullHistory(chainId: string): Promise<any[]> {
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(), API_TIMEOUT_MS)
  try {
    const res = await fetch(
      `${API}/market/config/market/list?chainId=${chainId}&includeInactive=true`,
      { signal: ctl.signal, headers: { Accept: 'application/json' } },
    )
    if (!res.ok) return []
    const body = (await res.json()) as any
    return Array.isArray(body?.data) ? body.data : []
  } catch {
    return []
  } finally {
    clearTimeout(timer)
  }
}

const TOTAL_SUPPLY_ABI = [
  {
    name: 'totalSupply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

const toConfig = (m: any): TermMaxMarketConfig | undefined => {
  const c = m?.contracts
  if (!c?.marketAddr || !c?.ftAddr || !c?.gtAddr) return undefined
  return {
    market: String(c.marketAddr).toLowerCase(),
    ft: String(c.ftAddr).toLowerCase(),
    xt: String(c.xtAddr ?? '').toLowerCase(),
    gt: String(c.gtAddr).toLowerCase(),
    collateral: String(c.collateralAddr ?? '').toLowerCase(),
    debtToken: String(c.underlyingAddr ?? '').toLowerCase(),
    maturity: Math.floor(Date.parse(m.maturity) / 1000),
    symbol: m.symbol,
  } as TermMaxMarketConfig
}

/**
 * Matured markets with anything still outstanding, for `chainId`.
 *
 * Returns the CACHED answer while it is fresh, and — deliberately — the STALE one
 * when a refresh fails: the set only ever shrinks, so a stale entry over-covers,
 * which costs a little gas. The alternative, returning empty, drops real positions.
 */
export async function fetchTermMaxMaturedResidue(
  chainId: string,
): Promise<TermMaxMarketConfig[]> {
  await refreshTermMaxMaturedResidue(chainId)
  return residueCache.get(chainId)?.markets ?? []
}

async function sweep(chainId: string): Promise<void> {
  if (!termMaxConfigByChain(chainId)) return
  const hit = residueCache.get(chainId)

  const nowSec = Math.floor(Date.now() / 1000)
  const all = await fetchFullHistory(chainId)
  const matured = all
    .map(toConfig)
    .filter((m): m is TermMaxMarketConfig => !!m && m.maturity <= nowSec)

  if (matured.length === 0) {
    // Upstream gave us nothing — a timeout, a bad response, or genuinely no
    // matured markets. Remember it either way, briefly, so the next reader does
    // not pay the same wait. Never clobber a good answer with an empty one.
    if (!hit || hit.failed) {
      residueCache.set(chainId, { markets: hit?.markets ?? [], at: Date.now(), failed: true })
    }
    return
  }

  // FT and GT together cover both sides: an unredeemed lender holds FT, an
  // unclosed borrower holds a GT. XT is not probed — it cannot outlive the FT it
  // was minted with, so a market with zero FT supply and zero GT supply is settled.
  const calls = matured.flatMap((m) => [
    { address: m.ft, name: 'totalSupply', params: [] },
    { address: m.gt, name: 'totalSupply', params: [] },
  ])

  let res: any[]
  try {
    res = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: TOTAL_SUPPLY_ABI as any,
      allowFailure: true,
    })) as any[]
  } catch {
    if (!hit || hit.failed) {
      residueCache.set(chainId, { markets: hit?.markets ?? [], at: Date.now(), failed: true })
    }
    return
  }

  const markets: TermMaxMarketConfig[] = []
  for (let i = 0; i < matured.length; i++) {
    const ft = res[2 * i]
    const gt = res[2 * i + 1]
    // A FAILED read is not a settled market. Keep it: over-covering costs gas,
    // under-covering loses somebody's position.
    const ftUnknown = typeof ft !== 'bigint'
    const gtUnknown = typeof gt !== 'bigint'
    if (ftUnknown || gtUnknown || ft > 0n || gt > 0n) markets.push(matured[i])
  }

  residueCache.set(chainId, { markets, at: Date.now() })
}
